/**
 * Checks shared by the scheme files.
 * @module banking/schemes/scheme-helpers
 */

import { validateBIC, type BankingValidationError } from '../validation';

/** Text without whitespace; null when nothing is left. */
export function compact(value?: string | null): string | null {
  const clean = value?.replace(/\s/g, '');
  return clean ? clean : null;
}

/** The value without the separators people write into numbers (`- . / _`); null when empty. */
export function compactDigits(value?: string | null): string | null {
  return compact(value?.replace(/[-./_]/g, ''));
}

/**
 * An IBAN as one key: upper case, every character outside A-Z and 0-9 dropped, so a written
 * `cz65-0800.0000` and `CZ650800 0000` are one account. Null when nothing is left.
 */
export function compactIban(value?: string | null): string | null {
  const clean = value?.toUpperCase().replace(/[^A-Z0-9]/g, '');
  return clean ? clean : null;
}

export function normalizeBic(bic?: string | null): string | null {
  return compact(bic)?.toUpperCase() ?? null;
}

/** BIC errors; none when the BIC is absent, as it is optional in every scheme. */
export function bicErrors(bic?: string | null): BankingValidationError[] {
  const clean = normalizeBic(bic);
  return clean ? validateBIC(clean).errors : [];
}

/** Upper-cased two-letter country code, or null when the value is not one. */
export function normalizeCountryCode(countryCode?: string | null): string | null {
  const clean = compact(countryCode)?.toUpperCase();
  return clean && /^[A-Z]{2}$/.test(clean) ? clean : null;
}

/** An error when a stated country differs from the one the scheme implies. */
export function countryMismatchErrors(
  countryCode: string | null | undefined,
  expected: string
): BankingValidationError[] {
  const stated = compact(countryCode)?.toUpperCase();
  if (!stated || stated === expected) return [];

  return [{ code: 'countryMismatch', message: `Country must be ${expected}` }];
}

/**
 * The country of a scheme used in several territories: the first one when none is stated,
 * else the stated one if the scheme serves it.
 */
export function territoryCountry(
  countryCode: string | null | undefined,
  territories: readonly string[]
): { country: string | null; errors: BankingValidationError[] } {
  if (!compact(countryCode)) return { country: territories[0], errors: [] };

  const country = normalizeCountryCode(countryCode);
  if (!country) {
    return {
      country: null,
      errors: [{ code: 'countryCodeInvalid', message: 'Country must be a two-letter code' }]
    };
  }
  if (!territories.includes(country)) {
    return {
      country: null,
      errors: [
        { code: 'countryMismatch', message: `Country must be one of ${territories.join(', ')}` }
      ]
    };
  }

  return { country, errors: [] };
}

export const REQUIRED_ERRORS = {
  bankIdentifier: { code: 'bankIdentifierRequired', message: 'Bank identifier is required' },
  accountNumber: { code: 'accountNumberRequired', message: 'Account number is required' }
} as const satisfies Record<string, BankingValidationError>;
